import { Link, type LinkProps } from '@tanstack/react-router';
import { type KeyboardEvent, type ReactElement } from 'react';

import { AppIcons, type AppIconName } from '@web/ui/core/atoms';
import { twcn } from '@web/ui/core/utils';

/* ────────────────────────── Tab ────────────────────────── */

type TabAccent = 'default' | 'brand';

type TabBase = {
  icon: AppIconName;
  label: string;
  isActive?: boolean;
  count?: number;
  /**
   * Active-tab emphasis. `default` underlines + tints with `foreground` (editor
   * tabs); `brand` underlines with the terracotta brand and tints the active
   * icon brand, with a plain mono count (the "Viajante" traveler tabs).
   */
  accent?: TabAccent;
};

type TabWithLink = TabBase & {
  to: LinkProps['to'];
  params?: LinkProps['params'];
  /** Search params to carry onto the link — preserves e.g. the board `view`. */
  search?: LinkProps['search'];
  onClick?: never;
  disabled?: false;
  hint?: never;
};

type TabWithButton = TabBase & {
  to?: never;
  params?: never;
  /** Local (non-router) tab selection — renders a `<button role="tab">`. */
  onClick: () => void;
  disabled?: false;
  hint?: never;
};

type TabDisabled = TabBase & {
  to?: never;
  params?: never;
  onClick?: never;
  disabled: true;
  hint?: string;
};

type TabBarTabProps = TabWithLink | TabWithButton | TabDisabled;

const tabClassName = (isActive: boolean, disabled: boolean, accent: TabAccent): string =>
  twcn(
    '-mb-px inline-flex items-center gap-1.5 border-b-2 border-transparent bg-transparent px-3.5 pb-2.5 pt-2.5 text-base font-medium text-muted-foreground no-underline transition-colors duration-fast ease-standard',
    'hover:text-foreground',
    isActive &&
      (accent === 'brand' ? 'border-primary text-foreground' : 'border-foreground text-foreground'),
    disabled && 'pointer-events-none text-faint-foreground',
  );

/**
 * Handles roving-tabindex keyboard nav inside a `role="tablist"` —
 * Arrow Left/Right move focus to the previous/next non-disabled tab,
 * Home/End jump to the first/last. The handler is shared by the
 * Link- and span-rendered tabs so both behave identically.
 */
function onTabKeyDown(event: KeyboardEvent<HTMLElement>): void {
  const { key } = event;
  if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Home' && key !== 'End') {
    return;
  }
  event.preventDefault();
  const current = event.currentTarget;
  const tablist = current.closest('[role="tablist"]');
  if (!tablist) return;
  const tabs = Array.from(tablist.querySelectorAll<HTMLElement>('[role="tab"]')).filter(
    (el) => el.getAttribute('aria-disabled') !== 'true',
  );
  if (tabs.length === 0) return;
  const index = tabs.indexOf(current);
  let nextIndex: number;
  if (key === 'ArrowLeft') {
    nextIndex = (index - 1 + tabs.length) % tabs.length;
  } else if (key === 'ArrowRight') {
    nextIndex = (index + 1) % tabs.length;
  } else if (key === 'Home') {
    nextIndex = 0;
  } else {
    nextIndex = tabs.length - 1;
  }
  tabs[nextIndex]?.focus();
}

/**
 * TabBar.Tab — single tab. When `disabled` is set the tab renders as
 * a non-interactive span with a lock glyph and optional `hint` title.
 * Otherwise it is a router link to `to`. `count` adds a small mono
 * pill after the label. The active tab carries `tabIndex=0` and the
 * others `tabIndex=-1` so arrow-key nav inside the tablist follows
 * the WAI-ARIA Authoring Practices.
 */
function Tab(props: Readonly<TabBarTabProps>): React.JSX.Element {
  const { icon, label, isActive = false, count, accent = 'default' } = props;
  const brandActive = accent === 'brand' && isActive;
  const countBadge =
    accent === 'brand' ? (
      <span className="font-mono text-tiny tabular-nums text-faint-foreground">{count}</span>
    ) : (
      <span className="rounded-pill bg-subtle px-1.5 font-mono text-card-id tabular-nums text-muted-foreground">
        {count}
      </span>
    );
  const content = (
    <>
      <AppIcons name={icon} size={14} className={brandActive ? 'text-primary' : undefined} />
      <span>{label}</span>
      {typeof count === 'number' && count > 0 ? countBadge : null}
      {props.disabled ? <AppIcons name="lock" size={11} /> : null}
    </>
  );

  if (props.disabled) {
    return (
      <span
        role="tab"
        aria-selected={isActive}
        aria-disabled
        tabIndex={-1}
        title={props.hint}
        className={tabClassName(isActive, true, accent)}
      >
        {content}
      </span>
    );
  }

  if (props.onClick) {
    return (
      <button
        type="button"
        role="tab"
        aria-selected={isActive}
        tabIndex={isActive ? 0 : -1}
        onKeyDown={onTabKeyDown}
        onClick={props.onClick}
        className={tabClassName(isActive, false, accent)}
      >
        {content}
      </button>
    );
  }

  return (
    <Link
      role="tab"
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      onKeyDown={onTabKeyDown}
      to={props.to as never}
      params={props.params as never}
      search={props.search as never}
      className={tabClassName(isActive, false, accent)}
    >
      {content}
    </Link>
  );
}

/* ────────────────────────── Root ────────────────────────── */

type TabElement = ReactElement<TabBarTabProps, typeof Tab>;
type TabChild = TabElement | null | false | undefined;

interface TabBarProps {
  ariaLabel: string;
  children: TabChild | TabChild[];
}

/**
 * TabBar — horizontal segmented nav for editor layouts (project /
 * squad). Owns the bottom hairline and the 28px gutter; `Tab`
 * children render the individual entries.
 */
function TabBarRoot({ ariaLabel, children }: Readonly<TabBarProps>): React.JSX.Element {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="flex border-b border-border-soft h-11 px-7"
    >
      {children}
    </div>
  );
}

export const TabBar = Object.assign(TabBarRoot, { Tab });
